import { ACTIVE_CANDIDATE_SLUGS, displayName } from "../../lib/candidates.ts";
import type { Board, BoardExpert, BoardTopic } from "../board.ts";
import type { Partners } from "../partners.ts";

// Candidate site / source links. TODO: real URLs come from the Phase 0 source
// inventory (docs/research). Empty href renders as "(enlace pendiente)".
const CANDIDATE_LINKS: Record<string, string> = {
  "abelardo-de-la-espriella": "",
  "paloma-valencia": "",
  "claudia-lopez": "",
  "sergio-fajardo": "",
  "ivan-cepeda": "",
};

let overlay: HTMLElement | null = null;

export function closeHelp(): void {
  overlay?.remove();
  overlay = null;
}

// Full-screen tabbed help: how to use · methodology · topics · experts ·
// herramientas (partner voter tools) · links.
export function openHelp(board: Board, partners: Partners): void {
  closeHelp();

  const el = document.createElement("div");
  el.className = "fixed inset-0 z-40 flex flex-col bg-white/95 p-4 md:p-8";

  const head = document.createElement("div");
  head.className = "mx-auto flex w-full max-w-3xl items-center gap-3";
  const h = document.createElement("h2");
  h.className = "text-2xl font-bold";
  h.textContent = "Ayuda";
  const close = document.createElement("button");
  close.className =
    "ml-auto rounded-lg border-2 border-black px-3 py-1 font-bold";
  close.textContent = "Cerrar ✕";
  close.addEventListener("click", closeHelp);
  head.append(h, close);

  const tabsBar = document.createElement("div");
  tabsBar.className = "mx-auto mt-3 flex w-full max-w-3xl flex-wrap gap-2";
  const body = document.createElement("div");
  body.className = "mx-auto mt-3 w-full max-w-3xl flex-1 overflow-auto";

  const tabs: { name: string; render: () => HTMLElement }[] = [
    { name: "Cómo usar", render: howTo },
    { name: "Metodología", render: () => methodology(board) },
    { name: "Temas", render: () => topicsView(board) },
    { name: "Expertos", render: () => expertsView(board) },
    { name: "Herramientas", render: () => toolsView(partners) },
    { name: "Enlaces", render: links },
  ];

  const buttons: HTMLButtonElement[] = [];
  const select = (i: number) => {
    buttons.forEach((b, j) =>
      b.className = "rounded-lg border-2 border-black px-3 py-1 font-bold " +
        (i === j ? "bg-black text-white" : "bg-white")
    );
    body.replaceChildren(tabs[i].render());
  };
  tabs.forEach((t, i) => {
    const b = document.createElement("button");
    b.textContent = t.name;
    b.addEventListener("click", () => select(i));
    buttons.push(b);
    tabsBar.appendChild(b);
  });

  el.append(head, tabsBar, body);
  document.body.appendChild(el);
  overlay = el;
  select(0);
}

// ── Tab renderers ──────────────────────────────────────────────────────────

function block(): HTMLElement {
  const d = document.createElement("div");
  d.className = "space-y-3 text-sm leading-relaxed";
  return d;
}

function howTo(): HTMLElement {
  const d = block();
  d.innerHTML = `
    <p>Te mueves por la fiesta y te acercas a cada puesto para hablar con la
       versión IA de cada candidato (basada en su material).</p>
    <ul class="list-disc pl-5 space-y-1">
      <li><b>Joystick</b> (abajo-izquierda): gíralo para rotar / avanzar.</li>
      <li><b>Flechas</b>: ↑/↓ avanzar y retroceder, ←/→ girar.</li>
      <li><b>Scroll</b>: vertical avanza/retrocede, horizontal gira.</li>
      <li>Acércate a un puesto y pulsa <b>💬 Hablar con…</b></li>
      <li><b>Mostrar mi candidato</b> levanta tu cartel — los demás lo ven.</li>
      <li>Lee los <b>carteles de expertos</b> en las paredes.</li>
    </ul>`;
  return d;
}

function methodology(board: Board): HTMLElement {
  const d = block();
  const h = document.createElement("h3");
  h.className = "text-lg font-bold";
  h.textContent = board.methodology.title || "Metodología";
  d.appendChild(h);
  for (const p of board.methodology.body) {
    const el = document.createElement("p");
    el.textContent = p;
    d.appendChild(el);
  }
  return d;
}

function topicCard(t: BoardTopic): HTMLElement {
  // Publishable only: topic + safe aggregates + curator headline/body.
  // No individual expert positions are ever rendered here.
  const card = document.createElement("div");
  card.className = "rounded-lg border-2 border-black p-3";

  const title = document.createElement("div");
  title.className = "font-bold";
  title.textContent = t.emoji ? `${t.emoji} ${t.title}` : t.title;
  card.appendChild(title);

  const stats: string[] = [];
  if (t.expertCount != null) stats.push(`${t.expertCount} expertos`);
  if (t.questionsAnswered != null) {
    stats.push(`${t.questionsAnswered} preguntas`);
  }
  if (t.meanAgreement != null) {
    stats.push(`acuerdo ${Math.round(t.meanAgreement * 100)}%`);
  }
  if (stats.length) {
    const s = document.createElement("div");
    s.className = "text-xs opacity-60";
    s.textContent = stats.join(" · ");
    card.appendChild(s);
  }

  const text = (t.headline?.trim() ? t.headline.trim() : "") +
    (t.body?.trim() ? `\n\n${t.body.trim()}` : "");
  const p = document.createElement("p");
  p.className = "mt-1 whitespace-pre-line text-neutral-700";
  p.textContent = text || "Contenido en preparación.";
  card.appendChild(p);

  return card;
}

function topicsView(board: Board): HTMLElement {
  const d = block();
  if (board.topics.length === 0) {
    d.textContent = "Aún no hay datos de expertos cargados.";
    return d;
  }
  for (const t of board.topics) d.appendChild(topicCard(t));
  return d;
}

function expertsView(board: Board): HTMLElement {
  // Roster only — names of who was consulted, never their positions.
  const d = block();
  const h = document.createElement("h3");
  h.className = "text-lg font-bold";
  const n = board.expertCount ?? board.experts?.length ?? 0;
  h.textContent = n ? `Expertos consultados (${n})` : "Expertos consultados";
  d.appendChild(h);

  if (board.experts && board.experts.length > 0) {
    const experts: BoardExpert[] = board.experts.map((e) =>
      typeof e === "string" ? { name: e } : e
    );
    const hasBios = experts.some((e) => e.description);
    const ul = document.createElement("ul");
    ul.className = hasBios ? "flex flex-col gap-2" : "columns-2 list-disc pl-5";
    for (const expert of experts) {
      const li = document.createElement("li");
      if (hasBios) {
        const name = document.createElement("span");
        name.className = "font-semibold";
        name.textContent = expert.name;
        li.appendChild(name);
        if (expert.description) {
          const desc = document.createElement("p");
          desc.className = "text-xs opacity-70";
          desc.textContent = expert.description;
          li.appendChild(desc);
        }
      } else {
        li.textContent = expert.name;
      }
      ul.appendChild(li);
    }
    d.appendChild(ul);
  } else {
    const p = document.createElement("p");
    p.className = "opacity-70";
    p.textContent =
      "La lista de expertos consultados se publicará próximamente.";
    d.appendChild(p);
  }

  const note = document.createElement("p");
  note.className = "mt-2 text-xs opacity-60";
  note.textContent =
    "Solo se publica quién fue consultado — nunca sus posiciones individuales.";
  d.appendChild(note);
  return d;
}

function toolsView(partners: Partners): HTMLElement {
  // Partner voter tools — the same links as the booths, findable without
  // walking up. Everything opens in a new tab.
  const d = block();
  const h = document.createElement("h3");
  h.className = "text-lg font-bold";
  h.textContent = "Herramientas para votar";
  d.appendChild(h);
  const intro = document.createElement("p");
  intro.className = "text-xs opacity-60";
  intro.textContent =
    "Recursos de medios y organizaciones aliadas, para ayudarte a decidir.";
  d.appendChild(intro);

  if (partners.partners.length === 0) {
    const p = document.createElement("p");
    p.className = "opacity-70";
    p.textContent = "Próximamente.";
    d.appendChild(p);
    return d;
  }

  for (const partner of partners.partners) {
    const card = document.createElement("div");
    card.className = "rounded-lg border-2 border-black p-3";

    const title = document.createElement("div");
    title.className = "font-bold";
    if (partner.url) {
      const a = document.createElement("a");
      a.href = partner.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.className = "underline";
      a.textContent = partner.name;
      title.appendChild(a);
    } else {
      title.textContent = partner.name;
    }
    card.appendChild(title);

    const ul = document.createElement("ul");
    ul.className = "mt-1 list-disc space-y-1 pl-5";
    for (const r of partner.resources) {
      const li = document.createElement("li");
      const a = document.createElement("a");
      a.href = r.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.className = "underline";
      a.textContent = r.title;
      li.appendChild(a);
      ul.appendChild(li);
    }
    card.appendChild(ul);
    d.appendChild(card);
  }
  return d;
}

function links(): HTMLElement {
  const d = block();
  const h = document.createElement("h3");
  h.className = "text-lg font-bold";
  h.textContent = "Candidatos y fuentes";
  d.appendChild(h);
  const ul = document.createElement("ul");
  ul.className = "list-disc space-y-1 pl-5";
  for (const slug of ACTIVE_CANDIDATE_SLUGS) {
    const li = document.createElement("li");
    const href = CANDIDATE_LINKS[slug];
    if (href) {
      const a = document.createElement("a");
      a.href = href;
      a.target = "_blank";
      a.rel = "noopener";
      a.className = "underline";
      a.textContent = displayName(slug);
      li.appendChild(a);
    } else {
      li.textContent = `${displayName(slug)} — (enlace pendiente)`;
    }
    ul.appendChild(li);
  }
  d.appendChild(ul);
  const note = document.createElement("p");
  note.className = "text-xs opacity-60";
  note.textContent =
    "Los enlaces oficiales se completan en la fase de investigación (docs/research).";
  d.appendChild(note);
  return d;
}
